import { Icon } from "@templates/design-system";
import { getComponent } from "../../../../client";
import { AbyssMedia } from "../../../../modules/content/components/AbyssMedia/AbyssMedia";
import { AbyssRichText } from "../../../../modules/content/components/AbyssRichText/AbyssRichText";
import type { IFooterBlockProps } from "./FooterBlock.props";

export const FooterBlock = ({
  contact,
  copyright,
  description,
  logo,
  navigationGroups,
  parentClass,
  secondaryLinks,
  socialLinks,
}: IFooterBlockProps) => {
  if (
    !logo &&
    !description &&
    !contact?.email &&
    !contact?.phone &&
    !contact?.address &&
    !navigationGroups?.length &&
    !socialLinks?.length &&
    !secondaryLinks?.length &&
    !copyright
  )
    return null;
  const Link = getComponent("Link");
  const linkClass =
    "inline-flex min-h-11 items-center font-abyss-mono text-sm text-frost/80 transition-colors hover:text-frost focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-flare";
  return (
    <footer className={`${parentClass || ""} bg-absolute-black text-frost`}>
      <div className="abyss-container py-16">
        <div className="grid gap-12 border-t border-frost/35 pt-8 lg:grid-cols-[1.2fr_2fr_0.8fr]">
          <div>
            {logo ? (
              <AbyssMedia
                className="mb-6 h-11 w-48"
                frameClassName="bg-transparent"
                imageClassName="object-contain object-left"
                media={logo}
                sizes="192px"
              />
            ) : null}
            {description ? (
              <AbyssRichText
                className="max-w-sm text-base text-frost/80 md:text-lg"
                content={description}
              />
            ) : null}
            {contact ? (
              <address className="mt-5 flex flex-col not-italic">
                {contact.email ? (
                  <a className={linkClass} href={`mailto:${contact.email}`}>
                    {contact.email}
                  </a>
                ) : null}
                {contact.phone ? (
                  <a className={linkClass} href={`tel:${contact.phone}`}>
                    {contact.phone}
                  </a>
                ) : null}
                {contact.address ? (
                  <p className="mt-2 font-abyss-mono text-sm leading-6 text-frost/80">
                    {contact.address}
                  </p>
                ) : null}
              </address>
            ) : null}
          </div>
          {navigationGroups?.length ? (
            <nav aria-label="Footer" className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {navigationGroups.map((group, index) => (
                <div key={index}>
                  <h2 className="mb-3 font-abyss-display text-sm font-medium">{group.header}</h2>
                  <ul>
                    {group.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link
                          aria-label={link.accessibilityLabel || undefined}
                          className={linkClass}
                          href={link.href}
                          rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                          target={link.openInNewTab ? "_blank" : undefined}
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          ) : null}
          {socialLinks?.length ? (
            <div className="flex flex-wrap content-start gap-2">
              {socialLinks.map((item, index) => (
                <Link
                  aria-label={item.link.accessibilityLabel || undefined}
                  className="inline-flex min-h-11 items-center gap-2 rounded-full border border-frost/35 px-4 font-abyss-mono text-sm hover:bg-frost hover:text-absolute-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-flare"
                  href={item.link.href}
                  key={index}
                  rel={item.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={item.link.openInNewTab ? "_blank" : undefined}
                >
                  {item.icon ? <Icon className="size-4" name={item.icon} /> : null}
                  {item.link.label}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
        <div className="mt-12 flex flex-col gap-3 border-t border-frost/35 pt-6 font-abyss-mono text-sm text-frost/70 sm:flex-row sm:items-center sm:justify-between">
          {secondaryLinks?.length ? (
            <div className="flex flex-wrap gap-x-5">
              {secondaryLinks.map((link, index) => (
                <Link
                  aria-label={link.accessibilityLabel || undefined}
                  className={linkClass}
                  href={link.href}
                  key={index}
                  rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={link.openInNewTab ? "_blank" : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          ) : null}
          {copyright ? <p>{copyright}</p> : null}
        </div>
      </div>
    </footer>
  );
};

FooterBlock.displayName = "Block::FooterBlock";
